import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import * as fromActions from './slice/portfolio.actions';
import {
  lazyLoadTransactionsForCashAccounts,
  loadInceptionPerformance,
  loadPreviousYearPerformance,
  loadYtdPerformance,
  setCurrentAssetAllocation
} from './slice/portfolio.actions';
import * as fromSelectors from './slice/portfolio.selectors';
import { PortfolioService } from './portfolio.service';
import { select, Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { NavigationEnd, Router, Event } from '@angular/router';
import { selectPortfoliosWhichAllowTrading } from '~/modules/accounts/slice';
import { CashAccount, LoadingStatus } from '@qvs/core';
import { AssetHelper } from '~/shared/helpers/asset-helper';

@Injectable()
export class PortfolioEffects {
  toggleFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.toggleFavoritePortfolio),
      withLatestFrom(this.store.pipe(select(fromSelectors.selectPortfolioFavoriteNrs))),
      exhaustMap(([action, existingFavoriteNumbers]) =>
        this.portfolioService
          .toggleFavorite(action.portfolio, existingFavoriteNumbers)
          .pipe(map((favoriteNumbers) => fromActions.toggleFavoritePortfolioSuccess({ favoriteNumbers })))
      )
    )
  );

  resetCurrentCashAccountOnSelectCurrentPortfolio$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.setCurrentPortfolio),
      filter((action) => !!action.portfolio?.portfolioNumber),
      map((action) => fromActions.setCurrentCashAccount({ portfolioNumber: action.portfolio.portfolioNumber }))
    )
  );

  loadCashTransactionsForPortfolio$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadPortfolioSuccess),
      mergeMap(({ portfolio }) => {
        const cashAllocation = AssetHelper.getCashAllocation(portfolio.assetAllocations);

        return (
          cashAllocation?.accounts?.map((account: CashAccount) => ({
            portfolioNumber: portfolio.portfolioNumber,
            cashAccountId: account.id
          })) ?? []
        );
      }),
      switchMap(({ portfolioNumber, cashAccountId }) =>
        of(lazyLoadTransactionsForCashAccounts({ portfolioNumber, cashAccountId, transactionsCount: undefined }))
      )
    )
  );

  loadPortfolio$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadPortfolio),
      switchMap(({ portfolioNumber }) =>
        this.portfolioService.loadPortfolio(portfolioNumber).pipe(
          map((portfolio) => fromActions.loadPortfolioSuccess({ portfolio })),
          catchError(() => of(fromActions.loadPortfolioFailure({ portfolioNumber })))
        )
      )
    )
  );

  loadTransactionsForCashAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.setCurrentCashAccount),
      switchMap((action) => {
        if (!action.cashAccountId) {
          return of(fromActions.loadTransactionsForCashAccountsSkip());
        }
        return this.portfolioService.loadTransactionsForCashAccount(action.cashAccountId).pipe(
          map((transactions) =>
            fromActions.loadTransactionsForCashAccountsSuccess({
              transactions,
              cashAccountId: action.cashAccountId,
              portfolioNumber: action.portfolioNumber
            })
          ),
          catchError(() =>
            of(
              fromActions.loadTransactionsForCashAccountsFailure({
                portfolioNumber: action.portfolioNumber,
                cashAccountId: action.cashAccountId
              })
            )
          )
        );
      })
    )
  );

  lazyLoadTransactionsForCashAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.lazyLoadTransactionsForCashAccounts),
      mergeMap((action) =>
        this.portfolioService.loadTransactionsForCashAccount(action.cashAccountId, action.transactionsCount).pipe(
          map((transactions) =>
            fromActions.loadTransactionsForCashAccountsSuccess({
              transactions,
              cashAccountId: action.cashAccountId,
              portfolioNumber: action.portfolioNumber
            })
          ),
          catchError(() =>
            of(
              fromActions.loadTransactionsForCashAccountsFailure({
                portfolioNumber: action.portfolioNumber,
                cashAccountId: action.cashAccountId
              })
            )
          )
        )
      )
    )
  );

  loadNextPortfolio$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadPortfolioSuccess, fromActions.loadPortfolioFailure),
      withLatestFrom(this.store.pipe(select(fromSelectors.selectPortfolios))),
      switchMap(([_action, portfolios]) => {
        const notLoadedPortfolio = portfolios.find(
          (portfolio) => portfolio.loadingStatus !== LoadingStatus.SUCCEEDED && portfolio.loadingStatus !== LoadingStatus.FAILED
        );
        if (notLoadedPortfolio) {
          return this.portfolioService.loadPortfolio(notLoadedPortfolio.portfolioNumber);
        } else {
          return of(null);
        }
      }),
      catchError(() => of(null)),
      switchMap((portfolio) => {
        if (portfolio) {
          return of(fromActions.loadPortfolioSuccess({ portfolio }));
        } else {
          return EMPTY;
        }
      })
    )
  );

  loadPortfolios$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadPortfolios),
      switchMap(() =>
        this.portfolioService.loadPortfolios().pipe(
          map((portfolios) => fromActions.loadPortfoliosSuccess({ portfolios })),
          catchError(() => of(fromActions.loadPortfoliosFailure()))
        )
      )
    )
  );

  // On the investment pages, if the current portfolio does not allow for trading, switch to the first portfolio which does.
  switchToPortfolioAllowedForTrading$ = createEffect(() =>
    this.router.events.pipe(
      filter((event: Event) => event instanceof NavigationEnd && event.url.includes('investment')),
      withLatestFrom(
        this.store.pipe(select(selectPortfoliosWhichAllowTrading)),
        this.store.pipe(select(fromSelectors.selectPortfolioCurrent))
      ),
      filter(
        ([_event, allowedPortfolios, currentPortfolio]) =>
          allowedPortfolios.length > 0 && !allowedPortfolios.includes(currentPortfolio)
      ),
      map(([_event, allowedPortfolios]) => fromActions.setCurrentPortfolio({ portfolio: allowedPortfolios[0] }))
    )
  );

  loadYtdPerformance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadYtdPerformance),
      mergeMap(({ portfolioNumber }) =>
        this.portfolioService.loadYtdPerformance(portfolioNumber).pipe(
          map((performance) => fromActions.loadYtdPerformanceSuccess({ performance })),
          catchError(() => of(fromActions.loadYtdPerformanceFailure({ portfolioNumber })))
        )
      )
    )
  );

  loadPreviousYearPerformance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadPreviousYearPerformance),
      mergeMap(({ portfolioNumber }) =>
        this.portfolioService.loadPreviousYearPerformance(portfolioNumber).pipe(
          map((performance) => fromActions.loadPreviousYearPerformanceSuccess({ performance })),
          catchError(() => of(fromActions.loadPreviousYearPerformanceFailure({ portfolioNumber })))
        )
      )
    )
  );

  loadInceptionPerformance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadInceptionPerformance),
      mergeMap(({ portfolioNumber }) =>
        this.portfolioService.loadInceptionPerformance(portfolioNumber).pipe(
          map((performance) => fromActions.loadInceptionPerformanceSuccess({ performance })),
          catchError(() => of(fromActions.loadInceptionPerformanceFailure({ portfolioNumber })))
        )
      )
    )
  );

  loadPerformances$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadPortfoliosSuccess),
      mergeMap(({ portfolios }) => portfolios),
      map((portfolio) => portfolio.portfolioNumber),
      switchMap((portfolioNumber) =>
        of(
          loadYtdPerformance({ portfolioNumber }),
          loadPreviousYearPerformance({ portfolioNumber }),
          loadInceptionPerformance({ portfolioNumber })
        )
      )
    )
  );

  setCurrentInstrument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.setCurrentInstrument),
      withLatestFrom(this.store.pipe(select(fromSelectors.selectPortfolioCurrent))),
      map(([action, portfolio]) =>
        portfolio?.assetAllocations?.find((assetAllocation) =>
          assetAllocation?.instruments?.some((instrument) => instrument.positionId === action.instrument.positionId)
        )
      ),
      switchMap((assetAllocation) => of(setCurrentAssetAllocation({ assetAllocation })))
    )
  );

  loadDepositaryBanks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadDepositaryBanks),
      switchMap(() =>
        this.portfolioService.loadDepositaryBanks().pipe(
          map((depositaryBanks) => fromActions.loadDepositaryBanksSuccess({ depositaryBanks })),
          catchError(() => of(fromActions.loadDepositaryBanksFailure()))
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly portfolioService: PortfolioService,
    private readonly router: Router
  ) {}
}
